import React from "react";
import PopupModal from "../../../UI/Popup";
import { TJobData } from "../../../../store/candidate/types";
import {
  applyForAJob,
  getJobCentreNameByVacancy,
  getJobTitleNameForCandidate,
} from "../../../../services/candidateJobsService";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Decline from "../../../UI/Icons/Decline";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { isFulfilled } from "@reduxjs/toolkit";
import { NOTIFICATION_TIME_OUT } from "../../../../constants/app";
import { showNotificationAction } from "../../../../store/application/slice";
import { getCandidateCalendarData } from "../../../../store/candidate/thunks";

interface ConflictConfirmProps {
  getCandidateJobList?: () => void;
  showApplyJobModal: boolean;
  setShowApplyJobModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClickClose?: () => void;
  vacancyData: TJobData;
  handleApplyForJob: (vacancyData: TJobData) => void;
  showConfirmApplyJobModal?: boolean;
  setShowConfirmApplyJobModal?: React.Dispatch<React.SetStateAction<boolean>>;
  showConflictConfirmModal?: boolean;
  setShowConflictConfirmModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAcceptedJobs?: (show: boolean) => void;
  isMobile?: boolean;
  onRefresh?: () => void;
}

const ConflictConfirmApply: React.FC<ConflictConfirmProps> = ({
  getCandidateJobList,
  showApplyJobModal,
  handleApplyForJob,
  onClickClose,
  setShowApplyJobModal,
  vacancyData,
  showConfirmApplyJobModal,
  setShowConfirmApplyJobModal,
  showConflictConfirmModal,
  setShowConflictConfirmModal,
  setShowAcceptedJobs,
  isMobile = false,
  onRefresh,
}) => {
  const dispatch = useAppDispatch();

  const profileData = useAppSelector(
    (state) => state.onboarding.userpersonaldetails
  );
  const maxDistanceAway = useAppSelector((state) => state.candidateJobs.maxDistanceAwayForMyCalendar)

  const handleClose = () => {
    setShowApplyJobModal(false);
    if (onClickClose) {
      onClickClose();
    }
  };

  const handleRemoveApplication = async(vacancyData: TJobData) => {
    const data = {
      action: "applied",
      uuid: vacancyData?.uuid,
      candidate_uuid: profileData?.uuid ?? "",
      selection_method: vacancyData?.selection_method,
      type: "Remove"
    };
    const removeApplicationResponse = await applyForAJob(dispatch, data);
    if(isFulfilled(removeApplicationResponse)){
      setShowApplyJobModal(false);
      getCandidateJobList && getCandidateJobList();
      onRefresh && onRefresh();
    }
  }

  const handleRemainAnApplicant= async(vacancyData: TJobData) => {
    const data = {
      action: "applied",
      uuid: vacancyData?.uuid,
      candidate_uuid: profileData?.uuid ?? "",
      selection_method: vacancyData?.selection_method,
      type: "Remain"
    };
    const remainApplicationResponse = await applyForAJob(dispatch, data);
    if(isFulfilled(remainApplicationResponse)){
      setShowApplyJobModal(false);
      setShowConfirmApplyJobModal && setShowConfirmApplyJobModal(true);      
      setTimeout(() => {
        setShowConfirmApplyJobModal && setShowConfirmApplyJobModal(false);
        getCandidateJobList && getCandidateJobList();
        isMobile && setShowAcceptedJobs && setShowAcceptedJobs(false); // For calendar module apply job
        isMobile && dispatch(getCandidateCalendarData({ distance_away: maxDistanceAway}));
        isMobile && onRefresh && onRefresh();
      }, NOTIFICATION_TIME_OUT);
    }else{
      if (
        remainApplicationResponse?.payload &&
        typeof remainApplicationResponse.payload === "object" &&
        "data" in remainApplicationResponse.payload &&
        (remainApplicationResponse.payload as any).data?.apply_restricted === true
      ) {
        setShowApplyJobModal(false);
        isMobile && setShowAcceptedJobs && setShowAcceptedJobs(false); // For calendar module apply job
        setShowConflictConfirmModal && setShowConflictConfirmModal(false);
        dispatch(showNotificationAction("You already applied 5 jobs for a day."))
        setTimeout(() => {
            isMobile && setShowAcceptedJobs && setShowAcceptedJobs(true); // For calendar module apply job
            isMobile && onRefresh && onRefresh();
        }, NOTIFICATION_TIME_OUT);
        
        return;
      }
    }
  }

  return (
    <PopupModal
      visible={!!showConflictConfirmModal}
      showCloseBtn={true}
      onClickClose={handleClose}
      customWidth={650}
    >
      <div className="gap-20 fs-22">We've found a job you have previously applied for</div>
      <div className="fs-13">
        Please confirm if you would like to remain an applied candidate for this job:{" "}
        {getJobTitleNameForCandidate(vacancyData)} at{" "}
        {getJobCentreNameByVacancy(vacancyData)}, {vacancyData?.centre?.address}{" "}
        {vacancyData?.timeframes &&
          formatTimeframes(vacancyData?.timeframes)?.map(
            (formatedTimeData, index) => (
              <span
                key={index}
                className={`fs-13 ${
                  formatedTimeData.alert ? "text-alert fw-700" : "fw-400"
                }`}
              >
                {formatedTimeData.label}
              </span>
            )
          )}
      </div>
      <div className="fs-13">Removing this application will not be noted on your record.</div>
      <div className="d-flex gap-20 justify-content-end">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-half-white"
          onClick={() => handleRemoveApplication(vacancyData)}
        >
          <Decline />
          {"Remove Application"}
        </CustomButton>

        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-teal text-black"
          onClick={() => handleRemainAnApplicant(vacancyData)}
        >
          <RoundedTick />
          {"Remain An Applicant"}
        </CustomButton>
      </div>
      {/* Add your modal content here */}
    </PopupModal>
  );
};

export default ConflictConfirmApply;
